import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Trigger = 'calculate' | 'promote_scenario' | 'manual';

interface VersionRow {
  readonly id: string;
  readonly versionNum: number;
  readonly trigger: Trigger;
  readonly note: string | null;
  readonly createdAt: string;
  readonly pdfGeneratedAt: string | null;
  readonly hasPdf: boolean;
  readonly scoreData: { grade?: string; score?: number; gradeColor?: string } | null;
}

interface Props {
  readonly analysisId: string;
  readonly projectName: string;
}

const TRIGGER_LABEL: Record<Trigger, string> = {
  calculate: 'Calculated',
  promote_scenario: 'Promoted from scenario',
  manual: 'Manual',
};

const TRIGGER_BADGE: Record<Trigger, string> = {
  calculate: 'bg-slate-100 text-slate-700',
  promote_scenario: 'bg-indigo-50 text-indigo-700',
  manual: 'bg-amber-50 text-amber-700',
};

/**
 * Collapsible version history panel for the analysis result page.
 *
 * Each row links to a downloadable PDF for that version (Step 2 stored
 * bytes on the version row; Step 6 promote-swap appends new rows). Lets
 * an analyst trace how the analysis evolved without leaving the page —
 * useful when stakeholders ask "what did the prior package look like?"
 * after a promote.
 */
export function VersionHistoryPanel({ analysisId, projectName }: Props) {
  const getToken = useSessionTokenGetter();
  const [versions, setVersions] = useState<VersionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${analysisId}/versions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as { versions: VersionRow[] };
        if (!cancelled) setVersions(data.versions);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [analysisId, getToken]);

  async function downloadVersion(versionNum: number) {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/analyses/${analysisId}/versions/${versionNum}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = projectName.replace(/[\\/:*?"<>|]/g, '').trim() || 'Analysis';
      a.download = `${safe} v${versionNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // Hide the panel for analyses with only one version — there's nothing
  // useful to surface, and it just adds visual noise. Show as soon as the
  // history is non-trivial.
  if (versions && versions.length <= 1) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50"
        aria-expanded={open}
      >
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Version history</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {versions ? `${versions.length} versions` : 'Loading…'}
            {versions && versions.length > 1 && (
              <>
                {' · latest is '}
                <strong className="text-slate-700">
                  v{versions[versions.length - 1]?.versionNum}
                </strong>
              </>
            )}
          </p>
        </div>
        <span className="text-sm text-slate-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          {error && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
          {!versions ? (
            <p className="text-sm text-slate-500">Loading versions…</p>
          ) : (
            <ol className="space-y-2">
              {[...versions].reverse().map((v) => {
                const date = new Date(v.createdAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                });
                return (
                  <li
                    key={v.id}
                    className="flex flex-wrap items-baseline justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-mono text-sm font-semibold text-slate-900">
                          v{v.versionNum}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${TRIGGER_BADGE[v.trigger]}`}
                        >
                          {TRIGGER_LABEL[v.trigger]}
                        </span>
                        {v.scoreData?.grade && v.scoreData.gradeColor && (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                            style={{ backgroundColor: v.scoreData.gradeColor }}
                            title={`Score ${v.scoreData.score?.toFixed(1) ?? ''}`}
                          >
                            {v.scoreData.grade}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">{date}</p>
                      {v.note && <p className="mt-1 text-xs text-slate-600 italic">{v.note}</p>}
                    </div>
                    <div className="shrink-0">
                      <button
                        type="button"
                        onClick={() => void downloadVersion(v.versionNum)}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        Download PDF →
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
